import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full border border-navy/20 bg-white px-3.5 py-3 text-sm text-navy focus:border-gold";
const EMPTY = { title: "", summary: "", scope: "", problems: [], approach: [], order: 0 };

export default function AdminAreas() {
  const [areas, setAreas] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/areas").then((r) => setAreas(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/areas/${editing.id}`, editing);
        toast.success("Área atualizada.");
      } else {
        await api.post("/admin/areas", editing);
        toast.success("Área criada.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível salvar a área.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/areas/${id}`);
      toast.success("Área excluída.");
      load();
    } catch (e) {
      toast.error("Não foi possível excluir.");
    }
  };

  return (
    <div data-testid="admin-areas">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="font-display text-3xl text-navy">Áreas de Atuação</h1>
          <p className="mt-2 text-sm text-inkgray">Edite as áreas exibidas no site: título, resumo, escopo, problemas e formas de atuação.</p>
        </div>
        <button
          data-testid="admin-area-new"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 bg-navy px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory hover:bg-navy-2"
        >
          <Plus size={14} /> Nova área
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {areas.map((a) => (
          <div key={a.id} className="flex flex-col justify-between gap-4 border border-navy/10 bg-white p-5 md:flex-row md:items-center" data-testid={`admin-area-row-${a.slug}`}>
            <div>
              <p className="font-semibold text-navy">
                0{a.order} — {a.title}
              </p>
              <p className="mt-1 text-xs text-inkgray">{a.summary}</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid={`admin-area-edit-${a.slug}`} onClick={() => setEditing(a)} className="inline-flex items-center gap-2 border border-navy/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-navy hover:border-gold">
                <Pencil size={12} /> Editar
              </button>
              <button data-testid={`admin-area-delete-${a.slug}`} onClick={() => remove(a.id)} className="border border-red-200 p-2 text-red-600 hover:bg-red-50" title="Excluir">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {areas.length === 0 && <p className="border border-dashed border-navy/20 bg-white px-6 py-10 text-sm text-inkgray">Nenhuma área cadastrada.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy/60 p-4 md:p-10" data-testid="admin-area-dialog">
          <div className="w-full max-w-2xl bg-white p-7 md:p-9">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy">{editing.id ? "Editar área" : "Nova área"}</h2>
              <button onClick={() => setEditing(null)} data-testid="admin-area-cancel-x" aria-label="Fechar" className="border border-navy/15 p-2 text-navy hover:border-gold">
                <X size={15} />
              </button>
            </div>
            <div className="mt-6 space-y-5">
              <div className="grid gap-5 sm:grid-cols-[1fr_120px]">
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Título *</p>
                  <input className={inputCls} data-testid="admin-area-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                </div>
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Ordem</p>
                  <input type="number" className={inputCls} data-testid="admin-area-order" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value, 10) || 0 })} />
                </div>
              </div>
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Resumo (aparece nos cartões)</p>
                <input className={inputCls} data-testid="admin-area-summary" value={editing.summary || ""} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
              </div>
              <div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Escopo dos serviços</p>
                <textarea rows={4} className={inputCls} data-testid="admin-area-scope" value={editing.scope || ""} onChange={(e) => setEditing({ ...editing, scope: e.target.value })} />
              </div>
              <StringList label="Problemas que podem envolver cada situação" field="problems" editing={editing} setEditing={setEditing} />
              <StringList label="Formas de atuação" field="approach" editing={editing} setEditing={setEditing} />
            </div>
            <div className="mt-8 flex gap-3">
              <button data-testid="admin-area-save" onClick={save} disabled={saving || !editing.title?.trim()} className="bg-navy px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-ivory hover:bg-navy-2 disabled:opacity-60">
                {saving ? "Salvando…" : "Salvar"}
              </button>
              <button data-testid="admin-area-cancel" onClick={() => setEditing(null)} className="border border-navy/20 px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-navy hover:border-gold">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StringList({ label, field, editing, setEditing }) {
  const items = editing[field] || [];
  const update = (i, v) => setEditing({ ...editing, [field]: items.map((x, j) => (j === i ? v : x)) });
  return (
    <div>
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">{label}</p>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input className={inputCls} data-testid={`admin-area-${field}-${i}`} value={item} onChange={(e) => update(i, e.target.value)} />
            <button
              data-testid={`admin-area-${field}-remove-${i}`}
              onClick={() => setEditing({ ...editing, [field]: items.filter((_, j) => j !== i) })}
              className="shrink-0 border border-red-200 p-2.5 text-red-600 hover:bg-red-50"
              aria-label="Remover"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <button
        data-testid={`admin-area-${field}-add`}
        onClick={() => setEditing({ ...editing, [field]: [...items, ""] })}
        className="mt-3 inline-flex items-center gap-2 border border-navy/20 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-navy hover:border-gold"
      >
        <Plus size={12} /> Adicionar
      </button>
    </div>
  );
}
